import React from 'react';
import { Star, Users, FileText, Volume2 } from 'lucide-react';
import PropTypes from 'prop-types';

export const JudgeCard = ({ 
  judge = {},
  isSelected = false,
  onClick = () => {}
}) => {
  const {
    name = 'Unknown Judge',
    photoUrl = null,
    comment = '',
    score = 0,
    scoreBreakdown = {}
  } = judge;

  return (
    <div 
      onClick={onClick}
      className={`
        cursor-pointer rounded-xl transition-all duration-300 h-[180px] overflow-hidden
        ${isSelected 
          ? 'flex-[0.6] bg-primary/20 shadow-lg p-3' 
          : 'flex-[0.2] bg-primary/10 hover:bg-primary/20 p-2'
        }
      `}
    >
      {isSelected ? (
        // Selected state layout
        <div className="h-full flex flex-col">
          {/* Header section */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              {photoUrl ? (
                <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center w-full h-full bg-gray-200">
                  <Users className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-base text-white truncate">{name}</h3>
              <div className="flex items-center gap-1">
                <Volume2 className="w-3 h-3 text-primary" />
                <p className="text-xs text-gray-400">Audio Channel Active</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {comment && (
                <div className="relative group">
                  <button className="p-1.5 rounded-lg bg-primary/20 hover:bg-primary/30">
                    <FileText className="h-4 w-4 text-gray-400" />
                  </button>
                  {/* Improved tooltip with background */}
                  <div className="absolute right-0 top-full mt-2 w-64 p-3 rounded-lg bg-gray-900/95 backdrop-blur-sm border border-gray-700 shadow-xl invisible group-hover:visible z-10">
                    <p className="text-sm text-gray-200">{comment}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Score section */}
          <div className="mt-3">
            <div className="flex items-baseline gap-1.5">
              <p className="text-2xl font-bold text-white">{score}</p>
              <p className="text-xs text-gray-400">points</p>
            </div>
          </div>

          {/* Score breakdown grid */}
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5">
            {Object.entries(scoreBreakdown).map(([category, value]) => (
              <div key={category} className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{category}</span>
                <span className="text-xs font-medium text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        // Deselected state layout
        <div className="h-full flex flex-col justify-between py-1">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full overflow-hidden mb-1.5">
              {photoUrl ? (
                <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center w-full h-full bg-gray-200">
                  <Users className="w-6 h-6 text-gray-400" />
                </div>
              )}
            </div>
            <h3 className="font-medium text-sm text-base-content truncate max-w-full">{name}</h3>
            <p className="text-xs text-base-content/70">Lead Judge</p>
          </div>

          <div className="text-center">
            <div className="flex items-baseline justify-center gap-1">
              <p className="text-2xl font-bold text-base-content">{score}</p>
              <p className="text-xs text-base-content/70">pts</p>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation(); // Prevent judge selection
                onClick(); // Select the judge for audio isolation
              }}
              className="mt-1.5 p-1.5 rounded-lg hover:bg-base-200"
              title="Listen to this judge"
            >
              <Volume2 className="h-3.5 w-3.5 text-base-content/70" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

JudgeCard.propTypes = {
  judge: PropTypes.shape({
    name: PropTypes.string,
    photoUrl: PropTypes.string,
    comment: PropTypes.string,
    score: PropTypes.number,
    scoreBreakdown: PropTypes.object
  }),
  isSelected: PropTypes.bool,
  onClick: PropTypes.func
};